import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileType,
  VerificationStatus,
  VerificationType,
} from "@mestergo/shared";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLocale } from "../lib/i18n";
import { api } from "../lib/api";
import { getTelegramWebApp } from "../lib/telegram";

interface VerificationSubmission {
  id: string;
  verificationType: VerificationType;
  status: VerificationStatus;
  notes?: string | null;
  submittedAt: string;
  files: Array<{
    id: string;
    fileUrl: string;
    fileType: FileType;
  }>;
}

interface PresignResponse {
  uploadUrl: string;
  publicUrl: string;
}

const statusColors: Record<VerificationStatus, string> = {
  [VerificationStatus.Pending]: "bg-orange-50 text-orange-600",
  [VerificationStatus.Approved]: "bg-emerald-50 text-emerald-700",
  [VerificationStatus.Rejected]: "bg-red-50 text-red-600",
  [VerificationStatus.Cancelled]: "bg-gray-100 text-gray-500",
};

const statusIcons: Record<VerificationStatus, string> = {
  [VerificationStatus.Pending]: "⏳",
  [VerificationStatus.Approved]: "✓",
  [VerificationStatus.Rejected]: "✕",
  [VerificationStatus.Cancelled]: "—",
};

export function HandymanVerificationPage() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [submissions, setSubmissions] = useState<VerificationSubmission[]>([]);
  const [verificationType, setVerificationType] = useState<VerificationType>(VerificationType.Identity);
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState<{ text: string; ok: boolean } | null>(null);

  const loadSubmissions = async () => {
    const { data } = await api.get<VerificationSubmission[]>("/verification/me");
    setSubmissions(data);
  };

  useEffect(() => {
    void loadSubmissions();
  }, []);

  useEffect(() => {
    const tg = getTelegramWebApp();
    if (!tg) return;
    tg.BackButton.show();
    const onBack = () => navigate("/handyman/profile");
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton.offClick(onBack);
      tg.BackButton.hide();
    };
  }, [navigate]);

  const inferContentType = (file: File): string => {
    if (file.type) return file.type;
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const map: Record<string, string> = {
      jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png",
      webp: "image/webp", heic: "image/heic", heif: "image/heif",
      pdf: "application/pdf",
    };
    return map[ext] ?? "image/jpeg";
  };

  const uploadFiles = async (selectedFiles: File[]) => {
    const uploadedUrls: string[] = [];
    for (const file of selectedFiles) {
      const contentType = inferContentType(file);
      const { data } = await api.post<PresignResponse>("/uploads/verification/presign", {
        fileName: file.name,
        contentType,
      });
      const uploadResponse = await fetch(data.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });
      if (!uploadResponse.ok) throw new Error("upload_failed");
      uploadedUrls.push(data.publicUrl);
    }
    return uploadedUrls;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    setNotice(null);
    try {
      const fileUrls = await uploadFiles(files);
      await api.post("/verification/me/submissions", {
        verificationType,
        notes: notes || undefined,
        fileUrls,
      });
      setNotes("");
      setFiles([]);
      setNotice({ text: t.verificationSubmitted, ok: true });
      await loadSubmissions();
    } catch {
      setNotice({ text: t.uploadError, ok: false });
    } finally {
      setIsSaving(false);
    }
  };

  const getTypeLabel = (type: VerificationType) => (
    type === VerificationType.Identity ? t.verificationIdentity : t.verificationProfessional
  );

  const getStatusLabel = (status: VerificationStatus) => {
    switch (status) {
      case VerificationStatus.Approved: return t.verificationStatusApproved;
      case VerificationStatus.Rejected: return t.verificationStatusRejected;
      case VerificationStatus.Cancelled: return t.verificationStatusCancelled;
      default: return t.verificationStatusPending;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-100 bg-white px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <button type="button" onClick={() => navigate("/handyman/profile")} className="text-sm font-semibold text-gray-500">
            {t.back}
          </button>
          <LanguageSwitcher />
        </div>
        <div className="text-2xl font-bold text-gray-900">{t.verificationTitle}</div>
        <div className="mt-1.5 text-sm text-gray-500">{t.verificationSubtitle}</div>
      </div>

      <div className="space-y-4 p-5">
        {/* Past submissions — shown first so status is immediately visible */}
        {submissions.length > 0 ? (
          <div className="rounded-2xl bg-white p-5 shadow-sm">
            <div className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-400">{t.verificationTitle}</div>
            <div className="space-y-3">
              {submissions.map((submission) => (
                <div key={submission.id} className="rounded-xl border border-gray-100 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {getTypeLabel(submission.verificationType)}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-400">
                        {new Date(submission.submittedAt).toLocaleString()}
                      </div>
                    </div>
                    <div className={`rounded-full px-3 py-1 text-xs font-semibold ${statusColors[submission.status]}`}>
                      {statusIcons[submission.status]} {getStatusLabel(submission.status)}
                    </div>
                  </div>
                  {submission.notes ? (
                    <div className="mt-2 text-sm text-gray-500">{submission.notes}</div>
                  ) : null}
                  <div className="mt-2 text-xs text-gray-400">
                    {submission.files.length} {t.verificationFiles}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Upload form */}
        <form className="rounded-2xl bg-white p-5 shadow-sm space-y-4" onSubmit={handleSubmit}>
          <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1">{t.submitVerification}</div>

          {/* Type selector */}
          <div className="grid grid-cols-2 gap-2">
            {[VerificationType.Identity, VerificationType.Professional].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setVerificationType(type)}
                className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-colors text-left ${
                  verificationType === type
                    ? "border-orange-500 bg-orange-50 text-orange-600"
                    : "border-gray-200 text-gray-600"
                }`}
              >
                {type === VerificationType.Identity ? "🪪" : "🏅"} {getTypeLabel(type)}
              </button>
            ))}
          </div>

          {/* Type hint */}
          <div className="rounded-xl bg-gray-50 px-4 py-3 text-xs text-gray-500">
            {verificationType === VerificationType.Identity
              ? "📋 Buletin de identitate sau pașaport — față și verso."
              : "🏅 Diplomă, certificat profesional sau autorizație de lucru."}
          </div>

          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={t.verificationNotesPlaceholder}
            rows={2}
            className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400 resize-none"
          />

          <label className="block cursor-pointer rounded-xl border-2 border-dashed border-gray-200 px-4 py-5 text-center text-sm text-gray-500 hover:border-orange-300 transition-colors">
            <div className="text-2xl mb-1">📎</div>
            {files.length > 0
              ? `${files.length} ${t.verificationFiles}`
              : t.uploadDocuments}
            <input
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
              className="hidden"
            />
          </label>

          {notice ? (
            <div className={`rounded-xl px-4 py-3 text-sm font-medium ${notice.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
              {notice.text}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={isSaving || files.length === 0}
            className="w-full rounded-xl bg-orange-500 px-4 py-4 text-sm font-semibold text-white disabled:opacity-50 active:bg-orange-600 transition-colors"
          >
            {isSaving ? t.loading : t.submitVerification}
          </button>
        </form>

        {submissions.length === 0 ? (
          <div className="rounded-xl bg-gray-50 px-4 py-5 text-center text-sm text-gray-400">
            {t.noVerificationSubmissions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
